import { useQuery } from "@tanstack/react-query";
import { motion } from "motion/react";
import { usePostHog } from "posthog-js/react";
import { useCallback, useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { twJoin } from "tailwind-merge";

import type { CommentDto, MessageReactionDto } from "@/api/types";
import { CommentField } from "@/components/CommentField/CommentField";
import {
  createMentionComposerValue,
  getStructuredMentionFields,
  type MentionComposerValue,
  type StructuredMentionFields,
} from "@/components/CommunityFeed/MentionComposer";
import { EntityPreview } from "@/components/EntityPreview/EntityPreview";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { formatDate, FormattedDate } from "@/components/FormattedDate/FormattedDate";
import { Gallery } from "@/components/Gallery/Gallery";
import { CheckIcon } from "@/components/Icons/CheckIcon";
import { CornerDownRightIcon } from "@/components/Icons/CornerDownRightIcon";
import type { FormImage } from "@/components/ImageInput/useImageInput";
import { useImageInput } from "@/components/ImageInput/useImageInput";
import { InactivePostWarning } from "@/components/InactivePostWarning/InactivePostWarning";
import { AutoTranslatedLabel } from "@/components/Label/AutoTranslatedLabel";
import { UserAvatarLink } from "@/components/UserAvatar/UserAvatar";
import { UserFullNameLink } from "@/components/UserFullNameLink/UserFullNameLink";
import { VideoFilePreview } from "@/components/VideoFilePreview/VideoFilePreview";
import { type FormVideo, isVideoUploaded, useVideoInput } from "@/components/VideoInput/useVideoInput";
import { Badge } from "@/design-system/Badge";
import { Box } from "@/design-system/Box";
import { Button } from "@/design-system/Button";
import { ContentBlock } from "@/design-system/ContentBlock";
import { Inline, Inline as LayoutInline } from "@/design-system/Inline";
import { Spinner } from "@/design-system/Spinner";
import { Stack } from "@/design-system/Stack";
import { Tooltip } from "@/design-system/Tooltip";
import { formatDistance } from "@/helpers/date";
import { unfurlDeepLinkFromContent } from "@/helpers/deep-link";
import { getTotalReactionAmount } from "@/helpers/reactions";
import { twResolve } from "@/helpers/tw-resolve";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useUploadVideo } from "@/hooks/Network/useUploadVideo";
import { useBool } from "@/hooks/useBool";
import { CommunityContentRail } from "@/product-patterns/community/CommunityContentRail";
import { communityFeedMutations } from "@/queries/community-feed/mutations";
import { useCommunityFeedQueries } from "@/queries/community-feed/queryOptions";
import { fileMutations } from "@/queries/files/mutations";
import { Trans, useTranslation } from "@/translations";

import { Globe02Icon } from "../Icons/Globe02Icon";
import { CommunityMessageReactCountButton } from "./CommunityMessageActivityButtons";
import { CommunityMessageReactionButton } from "./CommunityMessageReactionButton";
import { MentionContent } from "./MentionContent";
import type { CommunityMessageActivity } from "./types";

/** Creates a comment with both structured mention fields present, or with neither field. */
export type CreateCommentPayload = StructuredMentionFields & {
  parentId?: string;
  comment: string;
  images: FormImage[];
  videos: FormVideo[];
  failureMessage: string;
};
/** Edits a comment with both structured mention fields present, or with neither field. */
export type EditCommentPayload = StructuredMentionFields & {
  commentId: string;
  comment: string;
  images: FormImage[];
  videos: FormVideo[];
  failureMessage: string;
};
interface Props {
  message: CommunityMessageActivity;
  comment: CommentDto;
  onEdit: (comment: CommentDto) => void;
  onDelete: (comment: CommentDto) => void;
  onAddComment: (payload: CreateCommentPayload) => Promise<void>;
  onEditComment: (payload: EditCommentPayload) => Promise<void>;
  onViewCommentReactions: (commentId: string, amountReactions: number) => void;
}

/** Renders one comment, its structured mentions, activity, edit actions, and nested replies. */
export function Comment({
  message,
  comment,
  onAddComment,
  onEdit,
  onEditComment,
  onDelete,
  onViewCommentReactions,
}: Props): React.ReactNode {
  const [hideAllReplies, hideAllRepliesHandler] = useBool(true);
  const [showReplyField, showReplyFieldHandler] = useBool(false);
  const [replyImages, setReplyImages] = useState<FormImage[]>([]);
  const [replyVideos, setReplyVideos] = useState<FormVideo[]>([]);
  const [editingReply, setEditingReply] = useState<string | undefined>(undefined);
  const [replyMentionValue, setReplyMentionValue] = useState<MentionComposerValue>({ content: "" });
  const { t } = useTranslation();
  const showFlashToast = useFlashToast();
  const sessionUser = useSessionUser();
  const {
    addImages: addReplyImages,
    removeImage: removeReplyImage,
    removeImages: removeReplyImages,
  } = useImageInput({
    selectedImages: replyImages,
    onChange: setReplyImages,
  });
  const { uploadFormVideo } = useUploadVideo({
    onProgress: ({ name, progress }) => {
      setReplyVideos((prevState) =>
        prevState.map((video) => {
          if (!isVideoUploaded(video) && video.file.name === name) {
            return {
              ...video,
              uploadProgress: progress,
            };
          } else {
            return video;
          }
        }),
      );
    },
  });
  const {
    addVideos: addReplyVideos,
    removeVideo: removeReplyVideo,
    removeVideos: removeReplyVideos,
  } = useVideoInput({
    selectedVideos: replyVideos,
    onChange: setReplyVideos,
    uploadFn: uploadFormVideo,
  });
  const form = useForm<{
    replyValue: string;
  }>({
    defaultValues: {
      replyValue: "",
    },
  });
  const { setValue } = form;
  const communityFeedQueries = useCommunityFeedQueries();
  const {
    data: commentDetails,
    isFetching: isFetchingReplies,
    error: errorReplies,
  } = useQuery({
    ...communityFeedQueries.commentDetails({
      messageId: message.id,
      commentId: comment.id,
    }),
    enabled: !hideAllReplies,
  });
  const register = form.register;
  useEffect(() => {
    register("replyValue");
  }, [register]);
  useEffect(() => {
    if (errorReplies) {
      showFlashToast({
        variant: "alert",
        message: t("component.community-post.comments.reply.show-error"),
      });
    }
  }, [errorReplies, showFlashToast, t]);
  const replyValue = useWatch({ control: form.control, name: "replyValue" });
  async function onAddNestedComment() {
    if (editingReply) {
      await onEditComment({
        commentId: editingReply,
        comment: form.getValues("replyValue"),
        ...getStructuredMentionFields(replyMentionValue),
        images: replyImages,
        videos: replyVideos,
        failureMessage: t("component.community-post.comments.edit-error"),
      });
      setEditingReply(undefined);
    } else {
      await onAddComment({
        parentId: comment.id,
        comment: form.getValues("replyValue"),
        ...getStructuredMentionFields(replyMentionValue),
        images: replyImages,
        videos: replyVideos,
        failureMessage: t("component.community-post.comments.error"),
      });
    }
    removeReplyImages();
    removeReplyVideos();
    setValue("replyValue", "");
    setReplyMentionValue({ content: "" });
    showReplyFieldHandler.setFalse();
  }
  const handleEditComment = useCallback(
    ({ id, content, contentTemplate, mentions, image, videos }: CommentDto) => {
      setValue("replyValue", content || "");
      setReplyMentionValue(createMentionComposerValue({ content, contentTemplate, mentions }));
      if (image) {
        setReplyImages([image]);
      }
      if (videos.length > 0) {
        setReplyVideos(videos);
      }
      setEditingReply(id);
      showReplyFieldHandler.setTrue();
    },
    [setValue, setReplyImages, setEditingReply, showReplyFieldHandler],
  );
  const handleCancelEdit = useCallback(() => {
    setValue("replyValue", "");
    setReplyMentionValue({ content: "" });
    removeReplyImages();
    setEditingReply(undefined);
    showReplyFieldHandler.setFalse();
  }, [removeReplyImages, setValue, setEditingReply, showReplyFieldHandler]);
  const onChangeReplyContent = useCallback((value: string) => setValue("replyValue", value), [setValue]);
  const onChangeReplyMention = useCallback(
    (value: MentionComposerValue) => {
      setReplyMentionValue(value);
      setValue("replyValue", value.content);
    },
    [setValue],
  );
  const handleRemoveCommentImage = useCallback(
    (imageToBeRemoved: FormImage) => {
      removeReplyImage(imageToBeRemoved);
    },
    [removeReplyImage],
  );
  const hasSubContent =
    !!comment.latestReply ||
    isFetchingReplies ||
    (commentDetails?.replies && commentDetails.replies.length > 0) ||
    showReplyField;

  return (
    <Stack gap="md">
      <CommentInternal
        messageId={message.id}
        comment={comment}
        showReplyButton={comment.canReply}
        onAddNestedComment={showReplyFieldHandler.toggle}
        onEdit={onEdit}
        onDelete={onDelete}
        onViewCommentReactions={onViewCommentReactions}
      />
      {hasSubContent && (
        <section className="ml-space-xl flex flex-col gap-space-md" data-testid="comment-replies-section">
          {hideAllReplies && comment.latestReply ? (
            <>
              {comment.totalRepliesCount > 1 && (
                <span className="self-start">
                  <Button variant="subtle" onClick={hideAllRepliesHandler.setFalse}>
                    {t("component.community-post.comments.replies.show-replies", {
                      count: comment.totalRepliesCount - 1,
                    })}
                  </Button>
                </span>
              )}
              <CommentInternal
                messageId={message.id}
                comment={comment.latestReply}
                onEdit={handleEditComment}
                onDelete={onDelete}
                onViewCommentReactions={onViewCommentReactions}
              />
            </>
          ) : (
            <>
              {isFetchingReplies && (
                <motion.div
                  initial={{
                    opacity: 0,
                  }}
                  animate={{
                    opacity: 1,
                  }}
                  transition={{
                    delay: 0.4,
                  }}
                >
                  <Box padding={{ bottom: "sm" }}>
                    <LayoutInline justify="center">
                      <Spinner size="md" />
                    </LayoutInline>
                  </Box>
                </motion.div>
              )}
              {commentDetails &&
                (commentDetails.replies || [commentDetails.latestReply]).map((reply) => (
                  <CommentInternal
                    key={reply.id}
                    messageId={message.id}
                    comment={reply}
                    onEdit={handleEditComment}
                    onDelete={onDelete}
                    onViewCommentReactions={onViewCommentReactions}
                  />
                ))}
              {!hideAllReplies && commentDetails?.replies && (
                <span className="self-start">
                  <Button variant="subtle" onClick={hideAllRepliesHandler.setTrue}>
                    {t("component.community-post.comments.replies.hide-replies", {
                      count: comment.totalRepliesCount - 1,
                    })}
                  </Button>
                </span>
              )}
            </>
          )}
          {showReplyField && (
            <form className="flex items-end pb-space-sm">
              <CommentField
                tooltips={{
                  root:
                    !editingReply && message.isInactivePost
                      ? ({ onClose }) => <InactivePostWarning {...{ onClose, message }} />
                      : undefined,
                  send: editingReply
                    ? t("component.community-post.comments.edit.tooltip")
                    : t("component.community-post.comments.reply.tooltip"),
                }}
                placeholder={
                  editingReply
                    ? t("component.community-post.comments.edit.placeholder")
                    : t("component.community-post.comments.reply.placeholder")
                }
                user={sessionUser}
                value={replyValue}
                onChange={onChangeReplyContent}
                mentionValue={replyMentionValue}
                onMentionChange={onChangeReplyMention}
                messageId={message.id}
                images={replyImages}
                videos={replyVideos}
                onSubmit={onAddNestedComment}
                isEdit={editingReply !== undefined}
                onCancel={editingReply ? handleCancelEdit : undefined}
                allowedAttachments={["image", "video"]}
                onAddImages={addReplyImages}
                onRemoveImage={handleRemoveCommentImage}
                onAddVideo={addReplyVideos}
                onRemoveVideo={removeReplyVideo}
              />
            </form>
          )}
        </section>
      )}
    </Stack>
  );
}
interface InternalProps {
  messageId: string;
  comment: CommentDto;
  showReplyButton?: boolean;
  onAddNestedComment?: () => void;
  onEdit: (comment: CommentDto) => void;
  onDelete: (comment: CommentDto) => void;
  onViewCommentReactions: (commentId: string, amountReactions: number) => void;
}
function CommentInternal({
  messageId,
  comment,
  showReplyButton,
  onAddNestedComment,
  onEdit,
  onDelete,
  onViewCommentReactions,
}: InternalProps): React.ReactNode {
  const hasAutoTranslation = !!comment.autoTranslation;
  const [isTranslated, translationHandler] = useBool(() => hasAutoTranslation);

  const postHog = usePostHog();
  const { i18n, t } = useTranslation();
  const sessionUser = useSessionUser();
  const communityFeedQueries = useCommunityFeedQueries();
  const { data: translation, isLoading: isLoadingTranslation } = useQuery({
    ...communityFeedQueries.commentTranslation({
      messageId,
      commentId: comment.id,
      languageId: sessionUser.autoTranslationLanguage?.id || sessionUser.language.id,
    }),
    enabled: isTranslated && !hasAutoTranslation,
  });
  const updateReactionMutation = communityFeedMutations.useUpdateCommentReaction();
  const updateVideoViewCountMutation = fileMutations.useUpdateVideoViewCount();

  const handleNewReaction = (reactionType: MessageReactionDto["reactionType"] | undefined) => {
    updateReactionMutation.mutate({
      messageId: messageId,
      commentId: comment.id,
      data: {
        reactionType,
      },
    });
  };

  const handleOpenVideo = (videoId: string) => {
    updateVideoViewCountMutation.mutate({
      videoId,
    });
  };

  const getContent = () => {
    if (comment.deletedAt && !comment.content) {
      return t("component.community-post.comments.deleted");
    }

    if (isTranslated) {
      const activeTranslation = comment.autoTranslation ?? translation;
      if (activeTranslation) {
        return activeTranslation.content ?? "";
      }
    }

    return comment.content ?? "";
  };

  const content = getContent();
  const { content: contentFinal, entity: attachedEntity } = unfurlDeepLinkFromContent(content || "");
  const translatedContentTemplate = comment.autoTranslation?.contentTemplate ?? translation?.contentTemplate;

  const isTranslatable =
    !comment.languageIsoCode ||
    (sessionUser.autoTranslationLanguage
      ? comment.languageIsoCode !== sessionUser.autoTranslationLanguage.id
      : comment.languageIsoCode !== sessionUser.language.id);
  const canBeTranslated =
    !hasAutoTranslation && content && !comment.deletedAt && comment.author.id !== sessionUser.id && isTranslatable;
  const showTranslateToggle = (hasAutoTranslation && content && !comment.deletedAt) || canBeTranslated;

  return (
    <article
      className={twJoin("flex items-start gap-space-sm rounded-xs", comment.deletedAt && "text-text-secondary")}
      data-testid="comment-root"
    >
      <UserAvatarLink user={comment.author} />
      {/* Comment body */}
      <section className="flex flex-1 flex-col gap-space-sm overflow-hidden">
        <section
          className={twResolve(
            "flex flex-col gap-space-xs rounded-lg bg-background-secondary px-space-md py-space-sm",
            comment.isSolution && "bg-background-success-secondary",
          )}
        >
          <header className="flex w-full flex-wrap items-center justify-between">
            <span className="flex w-full items-center gap-space-sm">
              <span className="flex min-w-0 flex-1 items-center gap-space-xs">
                <UserFullNameLink user={comment.author} />
                {!!comment.author.deletedAt && <Badge variant="danger">{t("component.user.removed")}</Badge>}
              </span>
              {comment.isSolution && (
                <span
                  role="img"
                  aria-label={t("component.community-post.comments.solution")}
                  className="shrink-0 rounded-md bg-background-default p-space-xs text-text-success"
                >
                  <CheckIcon data-testid="solution-comment-icon" />
                </span>
              )}
              <span className="text-caption whitespace-nowrap text-text-secondary">
                {t("component.community-post.comments.posted-at", {
                  dateTime: formatDistance(t, {
                    start: new Date(comment.postedAt),
                  }),
                })}
              </span>
            </span>
            <Inline gap="sm" align="center">
              {comment.deletedAt && (
                <Tooltip
                  tooltip={
                    comment.deletedBy ? (
                      <Trans
                        i18nKey="component.community-post.tooltip.deleted-by"
                        values={{
                          name: comment.deletedBy.fullName,
                          dateTime: formatDate(i18n, "datetime", comment.deletedAt),
                        }}
                        components={{
                          b: <b className="text-caption-emphasis" />,
                        }}
                      />
                    ) : (
                      <FormattedDate date={comment.deletedAt} format="datetime" />
                    )
                  }
                >
                  <span className="text-caption text-text-secondary hover:underline">
                    {t("component.community-post.comments.deleted-at", {
                      time: formatDate(i18n, "datetime", comment.deletedAt),
                    })}
                  </span>
                </Tooltip>
              )}
            </Inline>
          </header>
          <Stack gap="sm">
            {/* Comment content */}
            {contentFinal && (
              <>
                <span
                  className={twJoin("flex-1", comment.deletedAt && contentFinal ? "line-through" : undefined)}
                  data-testid="comment-content"
                >
                  <MentionContent
                    content={contentFinal}
                    contentTemplate={isTranslated ? translatedContentTemplate : comment.contentTemplate}
                    mentions={comment.mentions}
                    source="comment"
                  />
                </span>
                {!comment.deletedAt && comment.updatedAt && (
                  <span className="text-caption text-text-tertiary">
                    {t("component.community-post.comments.last-edited-at", {
                      dateTime: formatDate(i18n, "datetime", comment.updatedAt),
                    })}
                  </span>
                )}
              </>
            )}
            {isTranslated && comment.autoTranslation?.isAutoTranslated && <AutoTranslatedLabel />}
            {showTranslateToggle && (
              <Button
                variant="ghostPrimary"
                icon={<Globe02Icon />}
                isLoading={isLoadingTranslation}
                onClick={translationHandler.toggle}
              >
                {isTranslated
                  ? t("component.community-post.comments.translate.original")
                  : t("component.community-post.comments.translate")}
              </Button>
            )}
            {(comment.image || comment.videos.length > 0 || attachedEntity) && (
              <Stack gap="sm">
                {comment.image && (
                  <figure data-testid="comment-attachment-image">
                    <Gallery images={[comment.image]} isDeleted={!!comment.deletedAt} />
                  </figure>
                )}
                {comment.videos.length > 0 && (
                  <figure data-testid="comment-attachment-video">
                    <VideoFilePreview onOpen={handleOpenVideo} video={comment.videos[0]} />
                  </figure>
                )}
                {attachedEntity && (
                  <CommunityContentRail>
                    <ContentBlock as="aside" width="flow" align="left" data-testid="comment-attached-entity-preview">
                      <EntityPreview
                        entityId={attachedEntity.payload.entityId}
                        entityType={attachedEntity.payload.entityType}
                        otherIds={{
                          folderId: attachedEntity.payload.folderId,
                        }}
                        onClick={() =>
                          postHog.capture("clicked_comm_feed_entity_preview", {
                            entity_type: attachedEntity.payload.entityType,
                          })
                        }
                      />
                    </ContentBlock>
                  </CommunityContentRail>
                )}
              </Stack>
            )}
          </Stack>
        </section>
        {/* Comment reactions & activities */}
        <Inline gap="md" align="center">
          {comment.canLike && (
            <CommunityMessageReactionButton
              size="sm"
              reaction={comment.requesterReactionType}
              onClickReaction={handleNewReaction}
            />
          )}
          {getTotalReactionAmount(comment.totalReactionCountByType) > 0 && (
            <CommunityMessageReactCountButton
              size="sm"
              counts={comment.totalReactionCountByType}
              isActive={false}
              isDisabled={false}
              onClick={() => onViewCommentReactions(comment.id, comment.totalLikesCount)}
            />
          )}
          {showReplyButton && (
            <Button
              variant="ghostSecondary"
              size="sm"
              title={t("component.community-post.comments.reply")}
              data-testid="reply-comment"
              onClick={onAddNestedComment}
              icon={<CornerDownRightIcon />}
            >
              {comment.totalRepliesCount ? comment.totalRepliesCount : null}
            </Button>
          )}
          {comment.canEdit && (
            <Button size="sm" variant="ghostSecondary" data-testid="edit-comment" onClick={() => onEdit(comment)}>
              {t("common.action.edit")}
            </Button>
          )}
          {(comment.canDeleteOwn || comment.canDeleteOther) && (
            <Button size="sm" variant="ghostSecondary" data-testid="delete-comment" onClick={() => onDelete(comment)}>
              {t("common.action.delete")}
            </Button>
          )}
        </Inline>
      </section>
    </article>
  );
}
